// src/pages/LiveBudget.tsx
// CM-03: Live Budget / Cost Control — BOQ baseline vs committed vs actual vs forecast-at-completion
// A13 CA Agent sidebar integrated
// Gap status: ABSENT → BUILT

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_CONTRACTS, MOCK_VARIATIONS } from '../utils/mockDataCommercial'
import { AgentSidebar } from '../components/AgentSidebar'
import { BarChart3, Printer } from 'lucide-react'

// ── Formatters ────────────────────────────────────────────────────
const fmtAUD = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n)
const fmtPct = (n: number) => `${n.toFixed(1)}%`

// ── Budget lines per job (seeded scenario data) ────────────────────
// In production these come from PO lines + timesheet actuals.
// Categories map to BOQ sections from the awarded tender estimate.
interface BudgetLine { category: string; budget: number; committed: number; actual: number; forecast: number }

const CATEGORY_SPLITS: Record<string, number> = {
  'Civil works':          0.22,
  'Electrical works':     0.28,
  'HV / Switching':       0.12,
  'Traffic control':      0.14,
  'Commissioning':        0.08,
  'Prelim & General':     0.10,
  'Subcontractors':       0.06,
}

// Per-job scenario factors (committed %, actual %) — varies by job progress
const JOB_FACTORS: Record<string, Record<string, [number, number]>> = {
  j1: { 'Civil works':[0.95,0.72], 'Electrical works':[0.78,0.55], 'HV / Switching':[0.60,0.45], 'Traffic control':[0.88,0.70], 'Commissioning':[0.30,0.10], 'Prelim & General':[0.85,0.68], 'Subcontractors':[0.70,0.55] },
  j2: { 'Civil works':[0.55,0.28], 'Electrical works':[0.42,0.20], 'HV / Switching':[0.35,0.15], 'Traffic control':[0.60,0.30], 'Commissioning':[0.10,0.02], 'Prelim & General':[0.50,0.25], 'Subcontractors':[0.40,0.18] },
  j4: { 'Civil works':[1.02,0.85], 'Electrical works':[0.90,0.72], 'HV / Switching':[0.98,0.80], 'Traffic control':[0.75,0.65], 'Commissioning':[0.85,0.60], 'Prelim & General':[0.92,0.78], 'Subcontractors':[0.88,0.70] },
  j5: { 'Civil works':[0.65,0.40], 'Electrical works':[0.58,0.35], 'HV / Switching':[0.20,0.08], 'Traffic control':[0.70,0.50], 'Commissioning':[0.55,0.30], 'Prelim & General':[0.60,0.42], 'Subcontractors':[0.50,0.32] },
}

function getBudgetLines(jobId: string): BudgetLine[] {
  const contract = MOCK_CONTRACTS.find(c => c.jobId === jobId)
  if (!contract) return []
  const varUplift = MOCK_VARIATIONS
    .filter(v => v.jobId === jobId && v.status === 'APPROVED')
    .reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
  const baseVal = contract.originalContractValue + varUplift
  const factors = JOB_FACTORS[jobId] ?? {}

  return Object.entries(CATEGORY_SPLITS).map(([cat, pct]) => {
    const budget    = baseVal * pct
    const [cf, af]  = factors[cat] ?? [0.5, 0.3]
    const committed = budget * Math.min(cf, 1.1)
    const actual    = budget * Math.min(af, cf * 0.99)
    const remaining = Math.max(0, committed - actual) * 1.05
    const forecast  = actual + remaining
    return { category: cat, budget, committed, actual, forecast }
  })
}

function rag(budget: number, forecast: number): 'green' | 'amber' | 'red' {
  const r = forecast / budget
  return r > 1.02 ? 'red' : r > 0.90 ? 'amber' : 'green'
}

const RAG: Record<string, { badge: string; bar: string; label: string }> = {
  green: { badge: 'bg-green-100 text-green-700', bar: 'bg-green-500', label: 'On budget'    },
  amber: { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-400', label: 'Watch'        },
  red:   { badge: 'bg-red-100 text-red-700',     bar: 'bg-red-500',   label: 'Over budget'  },
}

// ── Portfolio tab ─────────────────────────────────────────────────
function PortfolioTab() {
  const jobs = MOCK_CONTRACTS.map(con => {
    const lines    = getBudgetLines(con.jobId)
    const budget   = lines.reduce((s, l) => s + l.budget, 0)
    const committed= lines.reduce((s, l) => s + l.committed, 0)
    const actual   = lines.reduce((s, l) => s + l.actual, 0)
    const forecast = lines.reduce((s, l) => s + l.forecast, 0)
    const r        = rag(budget, forecast)
    return { ...con, budget, committed, actual, forecast, rag: r, committedPct: committed / budget * 100 }
  })
  const totBudget   = jobs.reduce((s, j) => s + j.budget, 0)
  const totCommitted= jobs.reduce((s, j) => s + j.committed, 0)
  const totActual   = jobs.reduce((s, j) => s + j.actual, 0)
  const atRisk      = jobs.filter(j => j.rag !== 'green').length

  return (
    <div className="space-y-4">
      {/* Portfolio KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total portfolio budget', value: fmtAUD(totBudget),   color: 'text-[#1F3864]' },
          { label: 'Total committed',         value: fmtAUD(totCommitted), color: 'text-gray-900'  },
          { label: 'Total actual to date',    value: fmtAUD(totActual),   color: 'text-gray-900'  },
          { label: 'Jobs at risk',            value: atRisk, color: atRisk > 0 ? 'text-amber-700' : 'text-green-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Jobs table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              {['Job', 'Type', 'Budget', 'Committed', '%', 'Actual', 'FAC', 'Variance', 'Status'].map(h => (
                <th key={h} className={`px-4 py-3 ${h === 'Job' ? 'text-left' : 'text-right'} ${h === 'Type' || h === 'Status' ? 'text-center' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {jobs.map(job => {
              const rm       = RAG[job.rag]
              const variance = job.budget - job.forecast
              return (
                <tr key={job.id} className={`hover:bg-gray-50 ${job.rag === 'red' ? 'bg-red-50/20' : job.rag === 'amber' ? 'bg-amber-50/10' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{job.jobNumber}</div>
                    <div className="text-gray-400 text-[10px]">{job.clientName}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      job.contractType === 'AS2124' ? 'bg-blue-50 text-blue-700' :
                      job.contractType === 'AS4000' ? 'bg-indigo-50 text-indigo-700' :
                      job.contractType === 'NEC4'   ? 'bg-purple-50 text-purple-700' :
                      'bg-teal-50 text-teal-700'
                    }`}>{job.contractType === 'PANEL_SOA' ? 'SOA' : job.contractType}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">{fmtAUD(job.budget)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtAUD(job.committed)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${job.committedPct > 95 ? 'bg-red-500' : job.committedPct > 80 ? 'bg-amber-400' : 'bg-blue-500'}`} style={{ width: `${Math.min(job.committedPct, 100)}%` }}/>
                      </div>
                      <span className={job.committedPct > 95 ? 'text-red-600 font-semibold' : job.committedPct > 80 ? 'text-amber-600 font-semibold' : 'text-gray-600'}>
                        {fmtPct(job.committedPct)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-gray-700">{fmtAUD(job.actual)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${job.rag === 'red' ? 'text-red-600' : job.rag === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>{fmtAUD(job.forecast)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${variance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {variance >= 0 ? '+' : ''}{fmtAUD(variance)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${rm.badge}`}>{rm.label}</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Job breakdown tab ─────────────────────────────────────────────
function JobBreakdownTab({ jobId }: { jobId: string }) {
  const contract  = MOCK_CONTRACTS.find(c => c.jobId === jobId)
  const lines     = getBudgetLines(jobId)
  if (!contract) return (
    <div className="text-center py-16 text-gray-400">
      <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-300"/>
      <p>No contract linked to this job.</p>
    </div>
  )
  const totBudget   = lines.reduce((s, l) => s + l.budget, 0)
  const totCommitted= lines.reduce((s, l) => s + l.committed, 0)
  const totActual   = lines.reduce((s, l) => s + l.actual, 0)
  const totForecast = lines.reduce((s, l) => s + l.forecast, 0)
  const totVariance = totBudget - totForecast
  const overallRag  = rag(totBudget, totForecast)
  const rm          = RAG[overallRag]

  const approvedVars = MOCK_VARIATIONS.filter(v => v.jobId === jobId && v.status === 'APPROVED')

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Budget (BOQ baseline)',    value: fmtAUD(totBudget),    color: 'text-[#1F3864]',  sub: 'Contract + approved vars' },
          { label: 'Committed (POs)',          value: fmtAUD(totCommitted), color: 'text-gray-900',   sub: `${fmtPct(totCommitted / totBudget * 100)} of budget` },
          { label: 'Actual to date',           value: fmtAUD(totActual),    color: 'text-gray-900',   sub: `${fmtPct(totActual / totBudget * 100)} of budget` },
          { label: 'Forecast at completion',   value: fmtAUD(totForecast),  color: overallRag === 'red' ? 'text-red-600' : overallRag === 'amber' ? 'text-amber-600' : 'text-green-700', sub: `${fmtPct(totForecast / totBudget * 100)} of budget` },
          { label: 'Variance (budget – FAC)',  value: (totVariance >= 0 ? '+' : '') + fmtAUD(totVariance), color: totVariance >= 0 ? 'text-green-700' : 'text-red-600', sub: totVariance >= 0 ? 'Under budget' : 'OVER BUDGET' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-lg font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Approved vars note */}
      {approvedVars.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-xs text-green-800 flex items-center gap-2">
          <span className="font-semibold">Budget includes {approvedVars.length} approved variation{approvedVars.length !== 1 ? 's' : ''}:</span>
          {fmtAUD(approvedVars.reduce((s, v) => s + (v.approvedAmount ?? 0), 0))} · Running sum {fmtAUD(contract.originalContractValue + approvedVars.reduce((s, v) => s + (v.approvedAmount ?? 0), 0))}
        </div>
      )}

      {/* Cost code table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Cost code breakdown</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              {['Cost code', 'Budget', 'Committed', '%', 'Actual', 'FAC', 'Variance', 'RAG'].map(h => (
                <th key={h} className={`px-4 py-2.5 ${h === 'Cost code' ? 'text-left' : 'text-right'} ${h === 'RAG' ? 'text-center' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {lines.map(line => {
              const r        = rag(line.budget, line.forecast)
              const rm2      = RAG[r]
              const variance = line.budget - line.forecast
              const pct      = line.committed / line.budget * 100
              return (
                <tr key={line.category} className={`hover:bg-gray-50 ${r === 'red' ? 'bg-red-50/20' : r === 'amber' ? 'bg-amber-50/10' : ''}`}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{line.category}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-700">{fmtAUD(line.budget)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-700">{fmtAUD(line.committed)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="w-10 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct > 95 ? 'bg-red-500' : pct > 80 ? 'bg-amber-400' : 'bg-blue-500'}`} style={{ width: `${Math.min(pct, 100)}%` }}/>
                      </div>
                      <span className={pct > 95 ? 'text-red-600 font-semibold' : pct > 80 ? 'text-amber-600' : ''}>{fmtPct(pct)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-700">{fmtAUD(line.actual)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r === 'red' ? 'text-red-600' : r === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>{fmtAUD(line.forecast)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-semibold ${variance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {variance >= 0 ? '+' : ''}{fmtAUD(variance)}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${rm2.badge}`}>{rm2.label}</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
              <td className="px-4 py-3 text-gray-900">TOTAL</td>
              <td className="px-4 py-3 text-right font-mono text-[#1F3864]">{fmtAUD(totBudget)}</td>
              <td className="px-4 py-3 text-right font-mono">{fmtAUD(totCommitted)}</td>
              <td className="px-4 py-3 text-right font-mono">{fmtPct(totCommitted / totBudget * 100)}</td>
              <td className="px-4 py-3 text-right font-mono">{fmtAUD(totActual)}</td>
              <td className={`px-4 py-3 text-right font-mono ${overallRag === 'red' ? 'text-red-600' : overallRag === 'amber' ? 'text-amber-600' : 'text-green-700'}`}>{fmtAUD(totForecast)}</td>
              <td className={`px-4 py-3 text-right font-mono ${totVariance >= 0 ? 'text-green-700' : 'text-red-600'}`}>{totVariance >= 0 ? '+' : ''}{fmtAUD(totVariance)}</td>
              <td className="px-4 py-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${rm.badge}`}>{rm.label}</span></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Contract ref strip */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-xs text-gray-600 flex flex-wrap gap-4">
        {[
          ['Contract', contract.contractNumber],
          ['Type', contract.contractType === 'PANEL_SOA' ? 'SOA Panel' : contract.contractType],
          ['Client', contract.clientName],
          ['Original value', fmtAUD(contract.originalContractValue)],
          ['LD rate', fmtAUD(contract.liquidatedDamagesRate) + '/day'],
          ['Retention held', fmtAUD(contract.retentionHeld) + ` (${contract.retentionPercent}%)`],
        ].map(([k, v]) => (
          <span key={k}><strong className="text-gray-700">{k}:</strong> {v}</span>
        ))}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export function LiveBudget() {
  useAuthStore()
  const [view, setView] = useState<'portfolio' | 'job'>('portfolio')
  const [jobId, setJobId]  = useState(MOCK_CONTRACTS[0]?.jobId ?? '')

  const printReport = () => {
    const rows = MOCK_CONTRACTS.map(con => {
      const lines    = getBudgetLines(con.jobId)
      const budget   = lines.reduce((s, l) => s + l.budget, 0)
      const committed= lines.reduce((s, l) => s + l.committed, 0)
      const actual   = lines.reduce((s, l) => s + l.actual, 0)
      const forecast = lines.reduce((s, l) => s + l.forecast, 0)
      const variance = budget - forecast
      const r        = rag(budget, forecast)
      return `<tr style="background:${r==='red'?'#fef2f2':r==='amber'?'#fffbeb':''}">
        <td><strong>${con.jobNumber}</strong><br><span style="color:#6b7280">${con.clientName}</span></td>
        <td style="text-align:right">${fmtAUD(budget)}</td>
        <td style="text-align:right">${fmtAUD(committed)}</td>
        <td style="text-align:right">${fmtPct(committed / budget * 100)}</td>
        <td style="text-align:right">${fmtAUD(actual)}</td>
        <td style="text-align:right">${fmtAUD(forecast)}</td>
        <td style="text-align:right;color:${variance>=0?'#16a34a':'#dc2626'}">${variance>=0?'+':''}${fmtAUD(variance)}</td>
        <td style="color:${r==='green'?'#16a34a':r==='amber'?'#d97706':'#dc2626'}">${RAG[r].label}</td>
      </tr>`
    }).join('')
    const html = `<!DOCTYPE html><html><head><title>Budget Variance Report</title>
    <style>body{font-family:Calibri,sans-serif;font-size:11px;margin:24px}h1{font-size:16px;color:#1F3864}
    table{width:100%;border-collapse:collapse;margin-top:12px}th{background:#1F3864;color:white;padding:6px 8px;font-size:10px}
    td{padding:5px 8px;border-bottom:1px solid #e5e7eb}tr:nth-child(even):not(.colored){background:#f9fafb}
    .footer{margin-top:20px;font-size:9px;color:#6b7280}</style></head><body>
    <h1>Budget Variance Report — MCB Civil Electrical Pty Ltd</h1>
    <p style="font-size:10px;color:#6b7280">Generated ${new Date().toLocaleString('en-AU')} · TrafficProcure MCBCE-K</p>
    <table><thead><tr><th>Job</th><th>Budget</th><th>Committed</th><th>Committed %</th><th>Actual</th><th>FAC</th><th>Variance</th><th>Status</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="footer">MCB Civil Electrical Pty Ltd ABN 44 128 993 087 · Confidential</div></body></html>`
    const w = window.open('', '_blank', 'width=1000,height=700')
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 300) }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Budget</h1>
          <p className="text-sm text-gray-500 mt-0.5">BOQ budget baseline · committed PO spend · actuals · forecast-at-completion · cost code variance</p>
        </div>
        <button onClick={printReport} className="btn-secondary text-sm flex items-center gap-2">
          <Printer className="w-4 h-4"/>Print variance report
        </button>
      </div>

      {/* View tabs */}
      {/* A13 CA Agent sidebar */}
      <AgentSidebar context={{
        page: 'live-budget',
        contracts:  MOCK_CONTRACTS,
        variations: MOCK_VARIATIONS,
        budget: { jobId, viewMode: view },
        platformDate: '2026-06-17',
      }} />

      {/* View tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        <button onClick={() => setView('portfolio')} className={`text-sm font-medium px-5 py-2 rounded-lg flex-1 transition-colors ${view === 'portfolio' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Portfolio summary
        </button>
        <button onClick={() => setView('job')} className={`text-sm font-medium px-5 py-2 rounded-lg flex-1 transition-colors ${view === 'job' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Job cost breakdown
        </button>
      </div>

      {/* Job selector (job view only) */}
      {view === 'job' && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 flex-shrink-0">Select job:</label>
          <select value={jobId} onChange={e => setJobId(e.target.value)} className="input text-sm flex-1 max-w-md">
            {MOCK_CONTRACTS.map(c => (
              <option key={c.jobId} value={c.jobId}>
                {c.jobNumber} — {c.clientName} ({c.contractType === 'PANEL_SOA' ? 'SOA Panel' : c.contractType})
              </option>
            ))}
          </select>
        </div>
      )}

      {view === 'portfolio' ? <PortfolioTab/> : <JobBreakdownTab jobId={jobId}/>}
    </div>
  )
}
